/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-13
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Configuration for keyboard shortcuts
 */
interface KeyboardShortcut {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
}

/**
 * Custom hook for global keyboard shortcuts
 * Provides common keyboard shortcuts for improved accessibility and user experience
 *
 * @example
 * // In App component
 * useKeyboardShortcuts();
 *
 * @returns void
 */
export const useKeyboardShortcuts = (): void => {
  const navigate = useNavigate();
  const { toggleMode } = useTheme();

  useEffect(() => {
    /**
     * Handle keyboard shortcut events
     * @param event - Keyboard event
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      // Define keyboard shortcuts
      const shortcuts: KeyboardShortcut[] = [
        {
          key: 'Escape',
          action: () => {
            // Close any open modals or return to home
            // For now, just blur the active element
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          },
          description: 'Close modals or blur active element',
        },
        {
          key: 'h',
          altKey: true,
          action: () => navigate('/'),
          description: 'Navigate to home page',
        },
        {
          key: 't',
          altKey: true,
          action: () => toggleMode(),
          description: 'Toggle theme (light/dark mode)',
        },
        {
          key: 'l',
          altKey: true,
          action: () => navigate('/login'),
          description: 'Navigate to login page',
        },
        {
          key: 'r',
          altKey: true,
          action: () => navigate('/register'),
          description: 'Navigate to register page',
        },
        {
          key: 'c',
          altKey: true,
          action: () => navigate('/contact'),
          description: 'Navigate to contact page',
        },
      ];

      // Check if any shortcut matches the current key combination
      const matchingShortcut = shortcuts.find(
        shortcut =>
          event.key === shortcut.key &&
          !!event.altKey === !!shortcut.altKey &&
          !!event.ctrlKey === !!shortcut.ctrlKey &&
          !!event.metaKey === !!shortcut.metaKey
      );

      if (matchingShortcut) {
        // Prevent default browser behavior
        event.preventDefault();
        event.stopPropagation();

        // Execute the shortcut action
        matchingShortcut.action();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, toggleMode]);
};

/**
 * Get available keyboard shortcuts for display in help or documentation
 * @returns Array of keyboard shortcut descriptions
 */
export const getKeyboardShortcuts = (): Array<{
  keys: string;
  description: string;
}> => [
  { keys: 'Escape', description: 'Close modals or blur active element' },
  { keys: 'Alt + H', description: 'Navigate to home page' },
  { keys: 'Alt + T', description: 'Toggle theme (light/dark mode)' },
  { keys: 'Alt + L', description: 'Navigate to login page' },
  { keys: 'Alt + R', description: 'Navigate to register page' },
  { keys: 'Alt + C', description: 'Navigate to contact page' },
];
